import React, {
    useState,
    useEffect,
    useCallback,
    useMemo,
    ChangeEvent,
} from 'react';
import {
    Button,
    Stack,
    ScrollArea,
    Loader,
    Text,
    Group,
    Checkbox,
    Box,
    TextInput,
    Tree,
    useTree,
    TreeNodeData,
    RenderTreeNodePayload,
} from '@mantine/core';
import { IconX, IconChevronRight, IconSearch } from '@tabler/icons-react';
import { useLazyQuery } from '@apollo/client';
import { useDebouncedValue } from '@mantine/hooks';
import { GET_OPERATIONS_HIERARCHY } from '../../../../apollo/queries/getOpsAreaHierarchy';
import {
    OpsAreaHierarchy,
    OpsDivision,
    OpsStore,
    Query,
} from '../../../../codegen/graphql';

interface StoreFilterTreeMenuProps {
    currentOperationsAreaIds: string[] | null;
    currentDivisionIds: string[] | null;
    currentStoreIds: string[] | null;
    onChange: (
        operationsAreaIds: string[] | null,
        divisionIds: string[] | null,
        storeIds: string[] | null,
    ) => void;
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    isOpen: boolean;
}

/** Mantine checkedState is leaf-only — expand applied ops/division filters to store leaves. */
function toLeafCheckedState(
    treeData: TreeNodeData[],
    operationsAreaIds: string[] | null,
    divisionIds: string[] | null,
    storeIds: string[] | null,
): string[] {
    const selected = new Set([
        ...(operationsAreaIds ?? []).map(id => `opsArea-${id}`),
        ...(divisionIds ?? []).map(id => `division-${id}`),
        ...(storeIds ?? []).map(id => `store-${id}`),
    ]);
    if (selected.size === 0) {
        return [];
    }

    const walk = (nodes: TreeNodeData[], inherited: boolean): string[] =>
        nodes.flatMap(node => {
            const included = inherited || selected.has(node.value);
            if (!node.children?.length) {
                return included ? [node.value] : [];
            }
            return walk(node.children, included);
        });

    return walk(treeData, false);
}

/** Prefer ops area → division → store so Apply stays compact (matches backend filter priority). */
function selectionFromCheckedNodes(
    treeData: TreeNodeData[],
    checkedValues: Set<string>,
): {
    operationsAreaIds: string[] | null;
    divisionIds: string[] | null;
    storeIds: string[] | null;
} {
    const operationsAreaIds = treeData
        .filter(opsArea => checkedValues.has(opsArea.value))
        .map(opsArea => opsArea.value.replace('opsArea-', ''));

    const uncheckedOpsAreas = treeData.filter(
        opsArea => !checkedValues.has(opsArea.value),
    );

    const divisionIds = uncheckedOpsAreas.flatMap(opsArea =>
        (opsArea.children ?? [])
            .filter(division => checkedValues.has(division.value))
            .map(division => division.value.replace('division-', '')),
    );

    const storeIds = uncheckedOpsAreas.flatMap(opsArea =>
        (opsArea.children ?? [])
            .filter(division => !checkedValues.has(division.value))
            .flatMap(division =>
                (division.children ?? [])
                    .filter(store => checkedValues.has(store.value))
                    .map(store => store.value.replace('store-', '')),
            ),
    );

    return {
        operationsAreaIds:
            operationsAreaIds.length > 0 ? operationsAreaIds : null,
        divisionIds: divisionIds.length > 0 ? divisionIds : null,
        storeIds: storeIds.length > 0 ? storeIds : null,
    };
}

function filterTreeBySearch(
    nodes: TreeNodeData[],
    searchTerm: string,
): TreeNodeData[] {
    return nodes
        .map(node => {
            const label =
                typeof node.label === 'string' ? node.label.toLowerCase() : '';
            const children = node.children
                ? filterTreeBySearch(node.children, searchTerm)
                : undefined;
            if (
                label.includes(searchTerm) ||
                (children && children.length > 0)
            ) {
                return { ...node, children };
            }
            return null;
        })
        .filter(Boolean) as TreeNodeData[];
}

export const StoreFilterTreeMenu: React.FC<StoreFilterTreeMenuProps> =
    React.memo(
        ({
            currentOperationsAreaIds,
            currentDivisionIds,
            currentStoreIds,
            onChange,
            setIsOpen,
            isOpen,
        }) => {
            const [searchValue, setSearchValue] = useState('');
            const [debouncedSearchValue] = useDebouncedValue(searchValue, 300);

            const [getHierarchy, { data, loading, error }] =
                useLazyQuery<Query>(GET_OPERATIONS_HIERARCHY, {
                    variables: { includeClosedStores: false },
                    fetchPolicy: 'cache-first',
                });

            useEffect(() => {
                if (isOpen) {
                    getHierarchy();
                }
            }, [isOpen, getHierarchy]);

            const treeData = useMemo((): TreeNodeData[] => {
                if (!data?.getOperationsHierarchy?.opsAreas) {
                    return [];
                }

                return data.getOperationsHierarchy.opsAreas.map(
                    (opsArea: OpsAreaHierarchy): TreeNodeData => ({
                        value: `opsArea-${opsArea.id!}`,
                        label: opsArea.name || 'Unknown Operations Area',
                        children: opsArea.divisions?.map(
                            (division: OpsDivision): TreeNodeData => ({
                                value: `division-${division.id!}`,
                                label: division.name || 'Unknown Division',
                                children: division.stores?.map(
                                    (store: OpsStore): TreeNodeData => ({
                                        value: `store-${store.id!}`,
                                        label:
                                            store.name ||
                                            store.storeNo ||
                                            'Unknown Store',
                                    }),
                                ),
                            }),
                        ),
                    }),
                );
            }, [data]);

            const filteredTreeData = useMemo(() => {
                const term = debouncedSearchValue.trim().toLowerCase();
                return term ? filterTreeBySearch(treeData, term) : treeData;
            }, [treeData, debouncedSearchValue]);

            const tree = useTree({ multiple: true });
            const { setCheckedState, expandAllNodes, uncheckAllNodes } = tree;

            useEffect(() => {
                if (!isOpen || treeData.length === 0) {
                    return;
                }
                setCheckedState(
                    toLeafCheckedState(
                        treeData,
                        currentOperationsAreaIds,
                        currentDivisionIds,
                        currentStoreIds,
                    ),
                );
            }, [
                isOpen,
                treeData,
                currentOperationsAreaIds,
                currentDivisionIds,
                currentStoreIds,
                setCheckedState,
            ]);

            useEffect(() => {
                if (debouncedSearchValue.trim()) {
                    expandAllNodes();
                }
            }, [debouncedSearchValue, expandAllNodes]);

            const renderTreeNode = useCallback(
                ({
                    node,
                    expanded,
                    hasChildren,
                    elementProps,
                    tree: treeController,
                }: RenderTreeNodePayload) => {
                    const checked = treeController.isNodeChecked(node.value);
                    const indeterminate = treeController.isNodeIndeterminate(
                        node.value,
                    );
                    const { className: labelClassName, ...labelProps } =
                        elementProps;

                    return (
                        <Box
                            {...labelProps}
                            className={`${labelClassName ?? ''} mb-2`}
                        >
                            <Group gap="xs" wrap="nowrap">
                                <Checkbox
                                    checked={checked}
                                    indeterminate={indeterminate}
                                    onChange={() => {
                                        if (checked) {
                                            treeController.uncheckNode(
                                                node.value,
                                            );
                                        } else {
                                            treeController.checkNode(
                                                node.value,
                                            );
                                        }
                                    }}
                                    onClick={e => e.stopPropagation()}
                                    size="sm"
                                />
                                <Group
                                    gap={5}
                                    onClick={() =>
                                        treeController.toggleExpanded(
                                            node.value,
                                        )
                                    }
                                    className={
                                        hasChildren
                                            ? 'cursor-pointer'
                                            : 'cursor-default'
                                    }
                                    wrap="nowrap"
                                >
                                    <Text size="sm">{node.label}</Text>
                                    {hasChildren && (
                                        <IconChevronRight
                                            size={14}
                                            className={`transition-transform duration-150 ease-in-out ${
                                                expanded ? 'rotate-90' : ''
                                            }`}
                                        />
                                    )}
                                </Group>
                            </Group>
                        </Box>
                    );
                },
                [],
            );

            const handleApply = useCallback(() => {
                const checkedValues = new Set(
                    tree
                        .getCheckedNodes()
                        .filter(node => node.checked)
                        .map(node => node.value),
                );
                const next = selectionFromCheckedNodes(treeData, checkedValues);
                onChange(
                    next.operationsAreaIds,
                    next.divisionIds,
                    next.storeIds,
                );
                setIsOpen(false);
            }, [tree, treeData, onChange, setIsOpen]);

            const handleClear = useCallback(() => {
                uncheckAllNodes();
                onChange(null, null, null);
            }, [uncheckAllNodes, onChange]);

            const handleClose = useCallback(() => {
                setCheckedState(
                    toLeafCheckedState(
                        treeData,
                        currentOperationsAreaIds,
                        currentDivisionIds,
                        currentStoreIds,
                    ),
                );
                setIsOpen(false);
            }, [
                setCheckedState,
                treeData,
                currentOperationsAreaIds,
                currentDivisionIds,
                currentStoreIds,
                setIsOpen,
            ]);

            return (
                <Stack
                    gap="xs"
                    p="sm"
                    className="max-h-[400px] min-w-[400px] overflow-hidden"
                    onClick={e => e.stopPropagation()}
                >
                    <Group gap={10} wrap="nowrap" className="w-full">
                        <TextInput
                            placeholder="Search..."
                            value={searchValue}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                e.stopPropagation();
                                setSearchValue(e.currentTarget.value);
                            }}
                            leftSection={<IconSearch size={16} />}
                            size="sm"
                            className="flex-[0_1_70%]"
                        />
                        <Button
                            variant="subtle"
                            color="red"
                            size="xs"
                            onClick={e => {
                                e.stopPropagation();
                                handleClear();
                            }}
                            leftSection={<IconX size={16} />}
                            className="flex-[1_1_30%] min-w-0"
                        >
                            Clear
                        </Button>
                    </Group>

                    <Box
                        className="flex-1 min-h-0 bg-primary/10"
                        py="xs"
                        px="sm"
                    >
                        <ScrollArea className="h-[200px] max-h-[250px]">
                            {loading && (
                                <div className="flex items-center justify-center h-40">
                                    <Loader size="sm" />
                                </div>
                            )}
                            {!loading && !!error && (
                                <Text c="red" size="sm">
                                    Error loading hierarchy
                                </Text>
                            )}
                            {!loading &&
                                !error &&
                                filteredTreeData.length > 0 && (
                                    <Tree
                                        data={filteredTreeData}
                                        tree={tree}
                                        levelOffset={20}
                                        expandOnClick={false}
                                        renderNode={renderTreeNode}
                                    />
                                )}
                            {!loading &&
                                !error &&
                                filteredTreeData.length === 0 && (
                                    <Text size="sm" c="dimmed">
                                        {debouncedSearchValue
                                            ? 'No matching stores found'
                                            : 'No store hierarchy data available'}
                                    </Text>
                                )}
                        </ScrollArea>
                    </Box>

                    <Group
                        justify="space-between"
                        className="w-full"
                        wrap="nowrap"
                        mt="sm"
                    >
                        <Button
                            className="text-primary border-primary hover:bg-primary/10"
                            variant="outline"
                            size="xs"
                            onClick={e => {
                                e.stopPropagation();
                                handleClose();
                            }}
                        >
                            Close
                        </Button>
                        <Button
                            size="xs"
                            onClick={e => {
                                e.stopPropagation();
                                handleApply();
                            }}
                        >
                            Apply
                        </Button>
                    </Group>
                </Stack>
            );
        },
    );

StoreFilterTreeMenu.displayName = 'StoreFilterTreeMenu';
